import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { useRadio } from '../context/RadioContext';

const { FiMusic, FiX, FiPlay, FiPause, FiVolume2, FiRadio } = FiIcons;

const RadioModal = ({ onClose }) => {
  const { t } = useLanguage();
  const { isPlaying, togglePlay, volume, setVolume } = useRadio();

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden relative"
      >
        {/* Abstract Background Decoration */}
        <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-br from-pink-500 to-rose-600 z-0" />
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 z-0 blur-2xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center pt-12 pb-8 px-8">
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors backdrop-blur-md"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>

          {/* Album Art / Icon */}
          <div className="w-32 h-32 bg-white rounded-3xl shadow-xl flex items-center justify-center mb-6 relative overflow-hidden group">
            <div className={`absolute inset-0 bg-gradient-to-tr from-pink-100 to-rose-50 opacity-50 transition-opacity ${isPlaying ? 'opacity-100' : 'opacity-50'}`} />
            
            {/* Visualizer bars simulation */}
            {isPlaying && (
              <div className="absolute inset-0 flex items-end justify-center gap-1 pb-4 opacity-30">
                {[...Array(5)].map((_, i) => (
                  <motion.div 
                    key={i}
                    animate={{ height: ['20%', '60%', '20%'] }}
                    transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.1, ease: "easeInOut" }}
                    className="w-3 bg-rose-500 rounded-t-sm"
                  />
                ))}
              </div>
            )}
            
            <SafeIcon icon={FiRadio} className={`text-5xl z-10 text-rose-500 transition-transform duration-500 ${isPlaying ? 'scale-110' : 'scale-100'}`} />
          </div>

          {/* Info */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-1">{t('radio_title')}</h2>
            <p className="text-slate-500 font-medium">{t('radio_subtitle')}</p>
            
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
              <span className="text-xs font-bold tracking-wider uppercase text-slate-400">
                {isPlaying ? t('live') : 'OFFLINE'}
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="w-full flex flex-col items-center gap-6">
            <button 
              onClick={togglePlay}
              className={`
                w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95
                ${isPlaying 
                  ? 'bg-white border-4 border-slate-100 text-slate-800 hover:bg-slate-50' 
                  : 'bg-rose-600 text-white hover:bg-rose-700 shadow-rose-500/30'}
              `}
            >
              <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-3xl ml-1" />
            </button>
            <p className="text-sm font-medium text-slate-400">
              {isPlaying ? t('playing') : t('click_to_play')}
            </p>

            {/* Volume */}
            <div className="w-full max-w-[200px] flex items-center gap-3 bg-slate-50 p-3 rounded-xl">
              <SafeIcon icon={FiVolume2} className="text-slate-400 text-lg" />
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.01" 
                value={volume} 
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-full accent-rose-500 h-1 bg-slate-200 rounded-full appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RadioModal;